import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type CustomReport, type MetricCatalogEntry } from "@/lib/api";
import { ErrorBox, PageHeader, Spinner } from "@/components/ui";

export function ObservabilityReportsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [reports, setReports] = useState<CustomReport[]>([]);
  const [catalog, setCatalog] = useState<MetricCatalogEntry[]>([]);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  const reload = async () => {
    const [r, c] = await Promise.all([api.listReports(id), api.metricCatalog(id)]);
    setReports(r.data);
    setCatalog(c.data);
  };

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        await reload();
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <Spinner label="Loading reports…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Observability"
        title="Custom Reports"
        description="Compose saved layouts from the metric catalog. Blocks are informational cards for now."
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Metric catalog</h3>
          <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
            {catalog.map((m) => {
              const on = selected.includes(m.id);
              return (
                <li key={m.id}>
                  <label className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-surface-container-low">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() =>
                        setSelected((prev) =>
                          on ? prev.filter((x) => x !== m.id) : [...prev, m.id],
                        )
                      }
                    />
                    <span>
                      <span className="font-medium text-text-primary">{m.name}</span>
                      <span className="ml-1 font-mono text-[10px] text-text-disabled">{m.id}</span>
                      <div className="text-xs text-text-muted">{m.description}</div>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="card p-4">
          <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Save report</h3>
          <label className="mb-3 block text-xs text-text-muted">
            Name
            <input
              className="mt-1 block w-full rounded border border-border-standard bg-surface-container-lowest px-2 py-1.5 text-sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <p className="mb-3 text-xs text-text-muted">{selected.length} block(s) selected</p>
          <button
            type="button"
            className="btn-primary"
            onClick={() =>
              void (async () => {
                try {
                  await api.saveReport(id, name, {
                    blocks: selected.map((metric_id) => ({ type: "stat", metric_id })),
                  });
                  setName("");
                  setSelected([]);
                  await reload();
                } catch (e) {
                  setError(e);
                }
              })()
            }
          >
            Save layout
          </button>
        </div>
      </div>

      <div className="card p-4">
        <h3 className="mb-3 font-sans text-sm font-semibold text-text-primary">Saved reports</h3>
        {reports.length === 0 ? (
          <p className="text-sm text-text-muted">No custom reports yet.</p>
        ) : (
          <ul className="space-y-2">
            {reports.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-2 rounded border border-border-standard px-3 py-2"
              >
                <div>
                  <div className="font-medium text-text-primary">{r.name}</div>
                  <div className="text-xs text-text-muted">
                    Updated {new Date(r.updated_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-secondary !py-1 text-xs"
                  onClick={() =>
                    void api.deleteReport(id, r.id).then(() => reload()).catch(setError)
                  }
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
